import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FaPlay, FaSignInAlt } from "react-icons/fa";
import { getSharePreview } from "../api/community";
import { hasValidToken } from "../api/storage";
import PublicLayout from "../components/site/PublicLayout";
import { Button, Card, Container, Spinner } from "../ui";

// Public landing page for a shared community mode (/m/:id). Shows the mode to
// anyone; opening it fully needs an account.
const ModeShare = () => {
  const { id } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["community", "share", id],
    queryFn: () => getSharePreview(id),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const signedIn = hasValidToken();
  const openPath = data ? `/app/modes/${data._id}` : "/app/community";

  return (
    <PublicLayout>
      <Container className="py-10 md:py-16 min-h-[60vh] flex items-center justify-center">
        <Card
          tone="solid"
          pad="none"
          className="w-full max-w-sm overflow-hidden shadow-2xl shadow-black/30"
        >
          {isLoading ? (
            <div role="status" className="flex justify-center py-20 text-primary">
              <Spinner size={32} />
            </div>
          ) : isError || !data ? (
            <div className="p-8 text-center">
              <h1 className="text-xl font-extrabold mb-2">هذه الفئة غير متاحة</h1>
              <p className="text-white/70 text-sm mb-6">
                ربما حُذفت أو أصبحت خاصة.
              </p>
              <Button to="/">الصفحة الرئيسية</Button>
            </div>
          ) : (
            <>
              {data.image && (
                <img
                  src={data.image}
                  alt={data.name}
                  className="w-full aspect-square object-cover"
                />
              )}
              <div className="p-6 text-center">
                <h1 className="text-2xl font-extrabold mb-1" dir="auto">
                  {data.name}
                </h1>
                <p className="text-white/55 text-sm mb-3">
                  {data.ownerName ? `${data.ownerName} · ` : ""}
                  {data.questionsCount} سؤال
                </p>
                {data.info && (
                  <p
                    className="text-white/70 text-sm leading-relaxed mb-5 whitespace-pre-wrap"
                    dir="auto"
                  >
                    {data.info}
                  </p>
                )}
                <Button
                  to={signedIn ? openPath : "/login"}
                  state={signedIn ? undefined : { from: openPath }}
                  size="lg"
                  block
                  icon={
                    signedIn ? (
                      <FaPlay className="text-sm" aria-hidden="true" />
                    ) : (
                      <FaSignInAlt aria-hidden="true" />
                    )
                  }
                >
                  {signedIn ? "افتح الفئة" : "سجّل الدخول لتلعبها"}
                </Button>
                {data.invitationCode && (
                  <p className="text-white/55 text-xs mt-4">
                    أو أضفها من التطبيق بالكود:{" "}
                    <span
                      className="text-primary font-bold tracking-widest"
                      dir="ltr"
                    >
                      {data.invitationCode}
                    </span>
                  </p>
                )}
              </div>
            </>
          )}
        </Card>
      </Container>
    </PublicLayout>
  );
};

export default ModeShare;
